// src/pages/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import GlassCard from '../components/UI/GlassCard';
import { Users, Calendar, ShoppingCart, Briefcase, Settings, DollarSign, TrendingUp, Dog, Activity, Bell, Receipt, Database, ArrowUpRight, Sparkles, Save } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { formatCurrency } from '../utils/formatters';
import { toast } from 'react-hot-toast';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import InteractiveWorkflow from '../components/Dashboard/InteractiveWorkflow';

// Componente para evitar el warning de ref en React 19
const SafeTippy = ({ children, ...props }) => {
    const [ref, setRef] = useState(null);
    return (
        <>
            <Tippy {...props} reference={ref} />
            {React.cloneElement(React.Children.only(children), { ref: setRef })}
        </>
    );
};

const Dashboard = () => {
    const { user, token, logout, can } = useAuth();
    const [stats, setStats] = useState({ kpis: {}, top_servicios: [], chart_ventas: [] });
    const [loading, setLoading] = useState(true);

    const COLORS = ['#8b5cf6', '#06b6d4', '#ec4899', '#10b981', '#f59e0b'];

    const [isVisualMode, setIsVisualMode] = useState(true); // FORZADO PARA PRUEBA DE VISIBILIDAD
    const [showNotifications, setShowNotifications] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const response = await axios.get('/backend/api/dashboard/stats.php');
            if (response.data.success) {
                setStats(response.data.data);
                setIsVisualMode(response.data.data.is_visual_mode);
            }
        } catch (error) {
            toast.error('Error al sincronizar KPIs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { 
        if (token) fetchData(); 
    }, [token, fetchData]);

    // Componente Gauge para KPIs Élite
    const KPI_Gauge = ({ title, value, percentage, sub, icon: Icon, gradient, colorClass }) => (
        <GlassCard className="relative overflow-hidden group border-white/5 shadow-2xl h-full min-h-[180px]">
             {/* Radial Progress Background */}
            <div className="absolute -right-8 -bottom-8 w-48 h-48 opacity-10 group-hover:opacity-20 transition-all duration-700">
                <svg className="w-full h-full" viewBox="0 0 36 36">
                    <path className="stroke-white/20 fill-none stroke-[2]" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    <path className={`fill-none stroke-[3] stroke-current ${colorClass} transition-all duration-1000 ease-out`} 
                          strokeDasharray={`${percentage}, 100`}
                          d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" 
                    />
                </svg>
            </div>
            
            <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex justify-between items-start">
                    <div className={`p-4 rounded-2xl ${gradient} shadow-lg shadow-black/30 group-hover:scale-110 transition-transform`}>
                        <Icon size={24} className="text-white" />
                    </div>
                    <div className="text-right">
                        <span className={`text-[9px] font-black tracking-[2px] uppercase ${colorClass}`}>{title}</span>
                        <p className="text-3xl font-black mt-1 leading-none tracking-tighter text-white">{value}</p>
                    </div>
                </div>
                <div className="mt-6">
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-white/30 font-bold uppercase tracking-widest">{sub}</span>
                        <span className={`text-[10px] font-black ${colorClass}`}>{percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className={`h-full ${gradient} transition-all duration-1000 ease-out`} style={{ width: `${percentage}%` }}></div>
                    </div>
                </div>
            </div>
        </GlassCard>
    );

    if (loading) return (
        <div className="h-screen w-full flex items-center justify-center p-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
    );

    const { kpis, top_servicios, chart_ventas } = stats;

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen relative overflow-hidden">
            
            {/* HUB DE NOTIFICACIONES (Lateral Desplegable) */}
            <div className={`fixed top-0 right-0 h-full w-[350px] z-[100] transition-all duration-500 ease-expo 
                            ${showNotifications ? 'translate-x-0 bubble-glow' : 'translate-x-full'}`}>
                <div className="h-full w-full bg-slate-900/95 backdrop-blur-3xl border-l border-white/10 p-8 shadow-[-20px_0_50px_rgba(0,0,0,0.5)]">
                    <div className="flex justify-between items-center mb-10">
                        <h3 className="text-xl font-black text-white flex items-center gap-3">
                            <Bell className="text-primary-light" /> Centro Histórico
                        </h3>
                        <button onClick={() => setShowNotifications(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-all">
                            <ArrowUpRight className="rotate-45" size={18} />
                        </button>
                    </div>
                    
                    <div className="space-y-6">
                        {[
                            { time: '10 min', msg: 'Factura INV-2026-0003 emitida: Juan Pérez', icon: Receipt, color: 'text-emerald-400' },
                            { time: '25 min', msg: 'Nueva Mascota Registrada: "Rocky"', icon: Dog, color: 'text-pink-400' },
                            { time: '1 hr', msg: 'Cita Completada: Baño & Corte', icon: Calendar, color: 'text-secondary' },
                            { time: '2 hrs', msg: 'Copia de seguridad DB generada', icon: Database, color: 'text-amber-400' }
                        ].map((n, i) => (
                            <div key={i} className="flex gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-all group">
                                <div className={`w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center ${n.color} transition-all group-hover:scale-110`}>
                                    <n.icon size={18} />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-white/80 font-medium leading-relaxed">{n.msg}</p>
                                    <span className="text-[10px] text-white/20 mt-1 block uppercase font-bold tracking-widest">{n.time}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Header VIP */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6 animate-in slide-in-from-top duration-700">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-black flex items-center gap-4 text-white">
                        <Dog className="text-primary-light" size={48} /> 
                        Dashboard <span className="text-primary-light italic font-medium">Pro</span>
                    </h1>
                    <p className="text-white/40 mt-2 text-sm lg:text-base font-medium flex items-center gap-2">
                        <Activity size={14} className="text-emerald-400" />
                        Operativa en vivo — {new Date().toLocaleDateString('es-PR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </p>
                </div>
                
                <div className="flex items-center gap-4">
                    {/* Botón de Notificaciones */}
                    <button 
                        onClick={() => setShowNotifications(true)}
                        className="relative w-14 h-14 rounded-2xl bg-slate-900 border border-white/5 flex items-center justify-center text-white/40 hover:text-white hover:border-primary/50 transition-all shadow-xl group"
                    >
                        <Bell size={24} className="group-hover:animate-swing" />
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-[10px] text-white flex items-center justify-center rounded-full font-bold border-2 border-slate-900 shadow-lg shadow-primary/40 pulse-ring">
                            4
                        </span>
                    </button>

                    <div className="flex items-center gap-3 glass p-2 pr-6 rounded-3xl self-end md:self-auto border-white/5">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-primary to-accent flex items-center justify-center font-black text-xl shadow-xl shadow-primary/20 text-white">
                            {user?.nombre?.[0]}
                        </div>
                        <div>
                            <span className="text-white/20 text-[10px] block uppercase font-black tracking-widest leading-none mb-1">Manejador/a</span>
                            <strong className="text-sm block text-white/90">{user?.nombre} {user?.apellido}</strong>
                        </div>
                    </div>
                </div>
            </header>

            {/* Menú de Acceso Rápido (Limpio) */}
            <div className="flex flex-wrap gap-4 mb-12 overflow-x-auto pb-2 no-scrollbar animate-in slide-in-from-left duration-700">
                {[
                    { label: 'Portal Visual', icon: Sparkles, path: '/portal', module: 'Comando Visual', color: 'text-primary-light' },
                    { label: 'Agenda', icon: Calendar, path: '/agenda', module: 'Agenda', color: 'text-secondary' },
                    { label: 'Facturación', icon: ShoppingCart, path: '/facturacion', module: 'Ventas', color: 'text-emerald-400' },
                    { label: 'Clientes', icon: Users, path: '/clientes', module: 'Clientes', color: 'text-primary-light font-bold' }
                ].filter(link => can(link.module)).map((link, i) => (
                    <SafeTippy key={link.label} content={`Acceder al módulo de ${link.label}`} theme="glass-premium" animation="scale">
                        <a href={link.path} className="glass glass-hover px-6 py-4 rounded-2xl flex items-center gap-3 font-black text-[12px] uppercase tracking-widest whitespace-nowrap border-white/5 active:scale-95 transition-all">
                            <link.icon size={18} className={link.color} />
                            {link.label}
                        </a>
                    </SafeTippy>
                ))}
            </div>

            {!can('Dashboard') && (
                <div className="p-20 rounded-[40px] border border-white/5 bg-slate-900/40 text-center animate-in zoom-in duration-1000">
                    <Sparkles size={64} className="text-primary-light mx-auto mb-6 animate-pulse" />
                    <h2 className="text-3xl font-black text-white mb-4">¡Hola, {user?.nombre}!</h2>
                    <p className="text-white/40 max-w-md mx-auto">Bienvenido a tu estación de trabajo. Utiliza el menú lateral para navegar por los módulos autorizados.</p>
                </div>
            )}

            {/* KPI Cards con Gauges */}
            {can('Dashboard') && (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-12 animate-in slide-in-from-bottom duration-700">
                    <KPI_Gauge 
                        title="Ventas de Hoy" 
                        value={formatCurrency(kpis.ventas_hoy || 0)} 
                        percentage={Math.min(100, Math.round(((kpis.ventas_hoy || 0) / 1000) * 100))}
                        sub={`Objetivo: $1,000`}
                        icon={DollarSign}
                        colorClass="text-emerald-400"
                        gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
                    />
                    <KPI_Gauge 
                        title="Facturación Mes" 
                        value={formatCurrency(kpis.ventas_mes || 0)} 
                        percentage={Math.min(100, Math.round(((kpis.ventas_mes || 0) / 10000) * 100))}
                        sub="Meta: $10,000"
                        icon={TrendingUp}
                        colorClass="text-primary-light"
                        gradient="bg-gradient-to-br from-primary to-indigo-700"
                    />
                    <KPI_Gauge 
                        title="Citas de Hoy" 
                        value={kpis.citas_hoy || 0} 
                        percentage={Math.min(100, Math.round(((kpis.citas_hoy || 0) / 20) * 100))}
                        sub="Capacidad: 20 citas"
                        icon={Calendar}
                        colorClass="text-secondary"
                        gradient="bg-gradient-to-br from-blue-500 to-cyan-600"
                    />
                    <KPI_Gauge 
                        title="Censo Animal" 
                        value={kpis.total_mascotas || 0} 
                        percentage={85}
                        sub="Pacientes Activos"
                        icon={Dog}
                        colorClass="text-pink-400"
                        gradient="bg-gradient-to-br from-pink-500 to-rose-700"
                    />
                </div>
            )}

            {/* Charts Section */}
            {can('Dashboard') && (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-12">
                    <GlassCard className="xl:col-span-2 shadow-2xl border-white/5 relative bg-slate-900/40" padding="40px">
                        <div className="flex justify-between items-center mb-10">
                            <div>
                                <h3 className="text-2xl font-black text-white flex items-center gap-3 tracking-tighter">
                                    <Activity className="text-secondary" />
                                    Monitor de Ingresos (7 Días)
                                </h3>
                                <p className="text-white/20 text-xs mt-1 font-bold uppercase tracking-widest">Rendimiento semanal de caja</p>
                            </div>
                            <div className="bg-emerald-500/10 px-4 py-2 rounded-xl border border-emerald-500/20 flex items-center gap-2">
                                <ArrowUpRight size={16} className="text-emerald-400" />
                                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Al alza</span>
                            </div>
                        </div>
                        
                        <div className="w-full h-[350px]">
                            <ResponsiveContainer>
                                <AreaChart data={chart_ventas}>
                                    <defs>
                                        <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4}/>
                                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.03)" />
                                    <XAxis dataKey="fecha" stroke="rgba(255,255,255,0.2)" fontSize={10} axisLine={false} tickLine={false} dy={10} />
                                    <YAxis stroke="rgba(255,255,255,0.2)" fontSize={10} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                                    <Tooltip 
                                        contentStyle={{ background: 'rgba(15, 23, 42, 0.95)', border: 'none', borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)', color: '#fff' }} 
                                        itemStyle={{ color: '#8b5cf6', fontWeight: 'bold' }}
                                    />
                                    <Area type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={4} fillOpacity={1} fill="url(#colorSales)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </GlassCard>

                    <GlassCard className="relative shadow-2xl border-white/5 bg-slate-900/40" padding="40px">
                        <h3 className="text-2xl font-black text-white mb-10 flex items-center gap-3 tracking-tighter justify-end">
                            Servicios Top
                            <ShoppingCart className="text-pink-400" />
                        </h3>
                        
                        <div className="w-full h-[250px]">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={top_servicios}
                                        dataKey="cantidad"
                                        nameKey="nombre"
                                        cx="50%" cy="50%"
                                        innerRadius={60}
                                        outerRadius={85}
                                        paddingAngle={8}
                                        stroke="none"
                                    >
                                        {top_servicios.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ background: '#0f172a', border: 'none', borderRadius: '12px', color: '#fff' }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="mt-8 space-y-3">
                            {top_servicios.map((s, i) => (
                                <div key={i} className="flex justify-between items-center bg-white/5 p-3 rounded-2xl border border-white/5">
                                    <div className="flex items-center gap-3">
                                        <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                                        <span className="text-xs font-bold text-white/70">{s.nombre}</span>
                                    </div>
                                    <span className="text-xs font-black text-white">{s.cantidad}</span>
                                </div>
                            ))}
                        </div>
                    </GlassCard>
                </div>
            )}
            
            <footer className="mt-12 pt-8 border-t border-white/5 flex justify-between items-center opacity-20 italic text-[10px] font-black uppercase tracking-[3px] text-white">
                <span>&copy; 2026 PawSpa Pro Enterprise</span>
                <span>Powered by Antigravity OS</span>
            </footer>
        </div>
    );
};

export default Dashboard;
